import type {
  FiveMinuteWindowEvent,
  PairDetail,
  PriceWindowSnapshot,
  TokenSummary,
  TradeSummary,
} from '../api/types'
import { isNonTonQuote, tradeRowUsdRate, tradeUsdPrice } from './tonQuote'

const WINDOW_SECONDS = 300

export function eventTapeToTrades(events: FiveMinuteWindowEvent[] | undefined): TradeSummary[] {
  if (!events) return []
  return events.map((event) => ({
    ts: event.ts,
    lt: event.lt,
    tx_hash: event.tx_hash,
    side: event.side === 'buy' || event.side === 'sell' ? event.side : 'unknown',
    base_amount: 0,
    quote_amount: event.quote_native,
    price: event.native_price ?? 0,
    price_usd: event.usd_price,
    trader_addr: '',
  }))
}

export function ageTokenFiveMinute(token: TokenSummary, nowSec: number): TokenSummary {
  if (!token.window_events_5m) return token
  const active = activeEvents(token.window_events_5m, nowSec)
  const window = agePriceWindow(token.price_window, token.window_events_5m, nowSec)
  const stats = summarize(active)
  return {
    ...token,
    txns_24h: stats.buys + stats.sells,
    txns_24h_buys: stats.buys,
    txns_24h_sells: stats.sells,
    // quote_native is the pool's own quote units — TON only on a TON-quoted
    // row. A pool row without TON keeps its TON-labelled field at the REST zero.
    volume_24h_native: isNonTonQuote(token.primary_quote_token)
      ? token.volume_24h_native
      : stats.native,
    volume_window_usd: stats.usd,
    price_window: window,
    price_change_24h_pct: window?.pct ?? token.price_change_24h_pct,
    window_events_5m: active,
  }
}

export function agePairFiveMinute(
  pair: PairDetail | null,
  trades: TradeSummary[],
  nowSec: number
): PairDetail | null {
  if (!pair || !pair.window_events_5m) return pair
  const events = mergeTapeAndTrades(pair.window_events_5m, trades)
  const active = activeEvents(events, nowSec)
  const window = agePriceWindow(pair.price_windows?.['5m'], events, nowSec)
  const stats = summarize(active)
  return {
    ...pair,
    txns_5m_buys: stats.buys,
    txns_5m_sells: stats.sells,
    volume_5m_buy_usd: stats.buyUSD,
    volume_5m_sell_usd: stats.sellUSD,
    price_change_5m_pct: window?.pct ?? pair.price_change_5m_pct,
    price_windows: window ? { ...pair.price_windows, '5m': window } : pair.price_windows,
    window_events_5m: active,
  }
}

export function appendFiveMinuteEvent(
  events: FiveMinuteWindowEvent[] | undefined,
  trade: TradeSummary
): FiveMinuteWindowEvent[] | undefined {
  if (!events) return events
  const quoteUSD = tradeQuoteUSD(trade)
  const next: FiveMinuteWindowEvent = {
    ts: trade.ts,
    lt: trade.lt,
    tx_hash: trade.tx_hash,
    side: trade.side,
    quote_native: trade.quote_amount,
    quote_usd: quoteUSD,
    native_price: trade.price,
    usd_price: tradeUsdPrice(trade),
  }
  return mergeTapeAndTrades(events, [trade]).map((event) =>
    event.ts === next.ts && event.tx_hash === next.tx_hash ? next : event
  )
}

function activeEvents(events: FiveMinuteWindowEvent[], nowSec: number): FiveMinuteWindowEvent[] {
  const cutoff = nowSec - WINDOW_SECONDS
  return events.filter((event) => event.ts > cutoff && event.ts <= nowSec)
}

function mergeTapeAndTrades(
  events: FiveMinuteWindowEvent[],
  trades: TradeSummary[]
): FiveMinuteWindowEvent[] {
  const byID = new Map(events.map((event) => [eventID(event), event]))
  for (const trade of trades) {
    const event: FiveMinuteWindowEvent = {
      ts: trade.ts,
      lt: trade.lt,
      tx_hash: trade.tx_hash,
      side: trade.side,
      quote_native: trade.quote_amount,
      quote_usd: tradeQuoteUSD(trade),
      native_price: trade.price,
      usd_price: tradeUsdPrice(trade),
    }
    byID.set(eventID(event), event)
  }
  return [...byID.values()].sort((a, b) => a.ts - b.ts || (a.lt ?? 0) - (b.lt ?? 0))
}

function eventID(event: Pick<FiveMinuteWindowEvent, 'ts' | 'tx_hash'>): string {
  return `${event.ts}:${event.tx_hash}`
}

function summarize(events: FiveMinuteWindowEvent[]): {
  buys: number
  sells: number
  native: number
  usd: number | null
  buyUSD: number | null
  sellUSD: number | null
} {
  let buys = 0
  let sells = 0
  let native = 0
  let usd = 0
  let buyUSD = 0
  let sellUSD = 0
  let usdComplete = true
  let buyUSDComplete = true
  let sellUSDComplete = true
  for (const event of events) {
    if (event.side === 'buy') buys++
    if (event.side === 'sell') sells++
    native += finiteOrZero(event.quote_native)
    if (finiteNonNegative(event.quote_usd)) {
      usd += event.quote_usd
      if (event.side === 'buy') buyUSD += event.quote_usd
      if (event.side === 'sell') sellUSD += event.quote_usd
    } else {
      usdComplete = false
      if (event.side === 'buy') buyUSDComplete = false
      if (event.side === 'sell') sellUSDComplete = false
    }
  }
  return {
    buys,
    sells,
    native,
    usd: usdComplete ? usd : null,
    buyUSD: buyUSDComplete ? buyUSD : null,
    sellUSD: sellUSDComplete ? sellUSD : null,
  }
}

function agePriceWindow(
  window: PriceWindowSnapshot | undefined,
  events: FiveMinuteWindowEvent[],
  nowSec: number
): PriceWindowSnapshot | undefined {
  if (!window) return window
  const previousAsOf = Date.parse(window.as_of) / 1000
  if (!Number.isFinite(previousAsOf) || nowSec <= previousAsOf) return window
  const oldCutoff = previousAsOf - WINDOW_SECONDS
  const newCutoff = nowSec - WINDOW_SECONDS
  let baselinePrice = window.baseline_price
  let baselineNative = window.baseline_native_price
  let baselineTS = window.baseline_source_ts
  let baselineLT = window.baseline_source_lt
  let status = window.status
  const statusBoundary = status === 'since_recovery' ? window.effective_start : baselineTS
  const baselineSourceSec = statusBoundary ? Date.parse(statusBoundary) / 1000 : Number.NaN
  if (
    (status === 'since_birth' || status === 'since_first_trade' || status === 'since_recovery') &&
    Number.isFinite(baselineSourceSec) &&
    newCutoff >= baselineSourceSec
  ) {
    baselineTS = new Date(newCutoff * 1000).toISOString()
    baselineLT = undefined
    status = 'ok'
  }
  for (const event of events) {
    if (!(event.ts > oldCutoff && event.ts <= newCutoff)) continue
    if (finitePositive(event.usd_price)) baselinePrice = event.usd_price
    if (finitePositive(event.native_price)) baselineNative = event.native_price
    baselineTS = new Date(event.ts * 1000).toISOString()
    baselineLT = event.lt
  }
  const pct = priceWindowChange(
    window.pct_denom,
    baselinePrice,
    window.current_price,
    baselineNative,
    window.current_native_price
  )
  return {
    ...window,
    as_of: new Date(nowSec * 1000).toISOString(),
    baseline_price: baselinePrice,
    baseline_native_price: baselineNative,
    baseline_source_ts: baselineTS,
    baseline_source_lt: baselineLT,
    pct,
    status,
  }
}

function priceWindowChange(
  denom: PriceWindowSnapshot['pct_denom'],
  baselineUSD: number | null | undefined,
  currentUSD: number | null | undefined,
  baselineNative: number | null | undefined,
  currentNative: number | null | undefined
): number | null {
  if (denom === 'native') return priceChange(baselineNative, currentNative)
  if (denom === 'usd') return priceChange(baselineUSD, currentUSD)
  return priceChange(baselineUSD, currentUSD) ?? priceChange(baselineNative, currentNative)
}

// The trade's quote leg in USD: quote × its OWN stamped quote→USD rate
// (tradeRowUsdRate) — the rule the window_events lane and the feed row share.
// A pending frame stays unpriced; its valued patch replaces it by identity.
function tradeQuoteUSD(trade: TradeSummary): number | null {
  if (trade.mc_status === 'pending') return null
  const rate = tradeRowUsdRate(trade)
  if (rate == null) return null
  return finiteOrZero(trade.quote_amount) * rate
}

function priceChange(baseline: number | null | undefined, current: number | null | undefined) {
  if (!finitePositive(baseline) || !finitePositive(current)) return null
  return ((current - baseline) / baseline) * 100
}

function finitePositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function finiteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function finiteOrZero(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0
}
